import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";

const imageUrl = (path) =>
  path?.startsWith("http") ? path : `${axios.defaults.baseURL || ""}${path || ""}`;

const addCacheBuster = (url) => {
  if (!url) return url;
  return `${url}${url.includes("?") ? "&" : "?"}cb=${Date.now()}`;
};

const isRequiredInput = (inputs, name) => {
  const input = inputs?.[name];
  return input === true || input?.required === true;
};

const getErrorMessage = (requestError, fallback) => {
  const detail = requestError?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (typeof detail?.message === "string") return detail.message;
  if (typeof requestError?.message === "string") return requestError.message;
  if (detail) return JSON.stringify(detail);
  return fallback;
};

const V4PModalTestModel = ({ open, model, onClose }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedTestImageIndex, setSelectedTestImageIndex] = useState(0);
  const [isModelTestImage, setIsModelTestImage] = useState(false);
  const [resultUrl, setResultUrl] = useState(null);
  const [showResultImage, setShowResultImage] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const previewRef = useRef(null);
  const imageViewportRef = useRef(null);
  const imageRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confidence, setConfidence] = useState(0.25);
  const [textPrompt, setTextPrompt] = useState("");
  const textInputRequired = isRequiredInput(model?.parameter_schema?.inputs, "text");
  const imageInputRequired = isRequiredInput(model?.parameter_schema?.inputs, "image");
  const usesTextInput = textInputRequired && !imageInputRequired;
  const confidenceParameter = (
    model?.parameter_schema?.parameters || model?.parameter_schema || {}
  ).confidence;
  const supportsConfidence = confidenceParameter?.type === "float";
  const confidenceMin = Number(confidenceParameter?.min ?? 0);
  const confidenceMax = Number(confidenceParameter?.max ?? 1);

  useEffect(() => {
    if (!open) return undefined;

    setSelectedImage(null);
    setResultUrl(null);
    setShowResultImage(false);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setError(null);
    setSelectedTestImageIndex(0);
    setIsModelTestImage(false);
    setConfidence(Number(confidenceParameter?.default ?? 0.25));
    setPreviewUrl(null);
    setTextPrompt(usesTextInput ? model?.test_images?.[0] || "" : "");
    return undefined;
  }, [confidenceParameter?.default, model, open, usesTextInput]);

  useEffect(() => () => {
    if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setSelectedImage(file);
    setIsModelTestImage(false);
    setPreviewUrl(URL.createObjectURL(file));
    setResultUrl(null);
    setShowResultImage(false);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setError(null);
  };

  const handleUseTestImage = async (index) => {
    const testImages = model?.test_images || [];
    if (!testImages[index]) return;

    try {
      const response = await axios.get(
        addCacheBuster(imageUrl(testImages[index])),
        { responseType: "blob" },
      );
      const file = new File(
        [response.data],
        `test-image-${index + 1}.png`,
        { type: response.data.type || "image/png" },
      );

      if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
      setSelectedTestImageIndex(index);
      setIsModelTestImage(true);
      setResultUrl(null);
      setShowResultImage(false);
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setError(null);
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Failed to load test image."));
    }
  };

  useEffect(() => {
    if (!usesTextInput && open && model?.test_images?.length && !selectedImage) {
      handleUseTestImage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, model, usesTextInput]);

  const handleNextTestImage = () => {
    const imageCount = model?.test_images?.length || 0;
    if (imageCount) {
      handleUseTestImage((selectedTestImageIndex + 1) % imageCount);
    }
  };

  const handlePreviousTestImage = () => {
    const imageCount = model?.test_images?.length || 0;
    if (imageCount) {
      handleUseTestImage((selectedTestImageIndex - 1 + imageCount) % imageCount);
    }
  };

  const handlePreviewWheel = (event) => {
    event.preventDefault();
    const previewBounds = event.currentTarget.getBoundingClientRect();
    const cursorX = event.clientX - (previewBounds.left + previewBounds.width / 2);
    const cursorY = event.clientY - (previewBounds.top + previewBounds.height / 2);

    setZoom((currentZoom) => {
      const nextZoom = Math.min(
        3,
        Math.max(1, Number((currentZoom + (event.deltaY < 0 ? 0.1 : -0.1)).toFixed(1))),
      );

      if (nextZoom === 1) {
        setPan({ x: 0, y: 0 });
      } else if (nextZoom !== currentZoom) {
        setPan((currentPan) => clampPan({
          x: cursorX - ((cursorX - currentPan.x) * nextZoom) / currentZoom,
          y: cursorY - ((cursorY - currentPan.y) * nextZoom) / currentZoom,
        }));
      }

      return nextZoom;
    });
  };

  const clampPan = (nextPan) => {
    const previewBounds = imageViewportRef.current?.getBoundingClientRect();
    const imageBounds = imageRef.current?.getBoundingClientRect();
    if (!previewBounds || !imageBounds) return nextPan;

    const maxX = Math.max(0, (imageBounds.width - previewBounds.width) / 2);
    const maxY = Math.max(0, (imageBounds.height - previewBounds.height) / 2);

    return {
      x: Math.min(maxX, Math.max(-maxX, nextPan.x)),
      y: Math.min(maxY, Math.max(-maxY, nextPan.y)),
    };
  };

  const handleImageMouseDown = (event) => {
    event.preventDefault();
    setIsDragging(true);
    setDragStart({ x: event.clientX, y: event.clientY, pan });
  };

  useEffect(() => {
    if (!isDragging || !dragStart) return undefined;

    const handleMouseMove = (event) => {
      setPan(clampPan({
        x: dragStart.pan.x + event.clientX - dragStart.x,
        y: dragStart.pan.y + event.clientY - dragStart.y,
      }));
    };
    const handleMouseUp = () => {
      setIsDragging(false);
      setDragStart(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragStart, isDragging]);

  const handlePredict = async () => {
    if (!model?.id) return;

    if (usesTextInput && !textPrompt.trim()) {
      setError("Enter a text prompt first.");
      return;
    }

    if (!usesTextInput && !selectedImage) {
      setError("Choose an image first.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      if (usesTextInput) {
        formData.append("text", textPrompt.trim());
      } else {
        formData.append("image", selectedImage);
      }
      if (supportsConfidence) {
        formData.append("parameters", JSON.stringify({ confidence }));
      }
      const { data } = await axios.post(
        `/v4p_models/${model.id}/predict`,
        formData,
      );

      if (!data.image) throw new Error("The model returned no result image.");
      setResultUrl(`data:image/png;base64,${data.image}`);
      setShowResultImage(true);
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Prediction failed."));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setSelectedImage(null);
    setPreviewUrl(null);
    setResultUrl(null);
    setShowResultImage(false);
    setTextPrompt("");
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setError(null);
    setIsModelTestImage(false);
    onClose();
  };

  if (!model) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: "60vw",
            height: "80vh",
            maxWidth: "none",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            border: "1px solid #cccccc",
            borderRadius: "12px",
          },
        },
      }}
    >
      <DialogTitle>Test {model.name}</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {!usesTextInput && <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexShrink: 0 }}>
          <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
            Import your own image to test the model.
          </Typography>
          <Button
            variant={isModelTestImage ? "contained" : "outlined"}
            sx={{
              ml: 2.5,
              textTransform: "none",
              borderRadius: "12px",
              borderColor: "#16a4b0",
              backgroundColor: isModelTestImage ? "#16a4b0" : "transparent",
              color: isModelTestImage ? "white" : "#16a4b0",
              boxShadow: "none",
            }}
            onClick={() => handleUseTestImage(selectedTestImageIndex)}
            disabled={loading || !(model.test_images?.length)}
          >
            Test Images
          </Button>
          <Button
            component="label"
            variant={!isModelTestImage ? "contained" : "outlined"}
            sx={{
              ml: 2.5,
              textTransform: "none",
              borderRadius: "12px",
              borderColor: "#16a4b0",
              backgroundColor: !isModelTestImage ? "#16a4b0" : "transparent",
              color: !isModelTestImage ? "white" : "#16a4b0",
              boxShadow: "none",
            }}
            disabled={loading}
          >
            Browse Image
            <input type="file" accept="image/jpeg,image/png" hidden onChange={handleFileChange} />
          </Button>
        </Box>}

        {usesTextInput && (
          <TextField
            label="Text prompt"
            value={textPrompt}
            onChange={(event) => {
              setTextPrompt(event.target.value);
              setError(null);
            }}
            multiline
            minRows={4}
            fullWidth
            disabled={loading}
            sx={{ mt: 1, mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        )}

        {supportsConfidence && <Box sx={{ mt: 1, mb: 1, flexShrink: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Confidence threshold
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {confidence.toFixed(2)}
            </Typography>
          </Box>

          <Slider
            value={confidence}
            min={confidenceMin}
            max={confidenceMax}
            step={0.01}
            valueLabelDisplay="auto"
            disabled={loading}
            onChange={(_, value) => setConfidence(value)}
            marks={[
              { value: confidenceMin, label: String(confidenceMin) },
              { value: confidenceMax, label: String(confidenceMax) },
            ]}
            sx={{
              color: "#16a4b0",
              "& .MuiSlider-thumb": { boxShadow: "none" },
            }}
          />
        </Box>}

        {(previewUrl || resultUrl || usesTextInput) && (
          <Box
            sx={{
              display: "flex",
              flex: 1,
              width: "100%",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 0,
              mt: 1,
              gap: 2,
            }}
          >
            {(previewUrl || resultUrl) && <Box
              ref={previewRef}
              onWheel={handlePreviewWheel}
              sx={{
                display: "flex",
                flex: 1,
                minHeight: 0,
                alignItems: "center",
                gap: 2,
                width: "100%",
                justifyContent: "center",
                overflow: "hidden",
                cursor: isDragging ? "grabbing" : zoom > 1 ? "grab" : "zoom-in",
              }}
            >
              {isModelTestImage && (
                <Button
                  onClick={handlePreviousTestImage}
                  disabled={loading}
                  sx={{ minWidth: 0, fontSize: "5rem", color: "#16a4b0", background: "none", boxShadow: "none" }}
                >
                  ‹
                </Button>
              )}
              <Box
                ref={imageViewportRef}
                sx={{
                  display: "flex",
                  flex: 1,
                  alignSelf: "stretch",
                  height: "100%",
                  minWidth: 0,
                  minHeight: 0,
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  ref={imageRef}
                  src={showResultImage && resultUrl ? resultUrl : previewUrl}
                  alt={showResultImage && resultUrl ? "Prediction result" : "Selected input"}
                  onMouseDown={handleImageMouseDown}
                  sx={{
                    display: "block",
                    width: "auto",
                    height: "auto",
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: "center",
                    transition: "transform 120ms ease-out",
                    userSelect: "none",
                    border: "1px solid #ddd",
                    borderRadius: 2,
                  }}
                />
              </Box>
              {isModelTestImage && (
                <Button
                  onClick={handleNextTestImage}
                  disabled={loading}
                  sx={{ minWidth: 0, fontSize: "5rem", color: "#16a4b0", background: "none", boxShadow: "none" }}
                >
                  ›
                </Button>
              )}
            </Box>}
            {isModelTestImage && (
              <Typography variant="body2" color="text.secondary">
                Test image {selectedTestImageIndex + 1} of {model.test_images.length}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1, mt: 1, flexShrink: 0 }}>
              <Button
                variant="contained"
                sx={{
                  textTransform: "none",
                  borderRadius: "12px",
                  backgroundColor: "#16a4b0",
                  color: "white",
                  boxShadow: "none",
                }}
                onClick={handlePredict}
                disabled={(usesTextInput ? !textPrompt.trim() : !selectedImage) || loading}
              >
                {loading ? <CircularProgress size={24} sx={{ color: "black" }} /> : "Run Inference"}
              </Button>
              {resultUrl && !usesTextInput && (
                <Button
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    borderRadius: "12px",
                    borderColor: "#16a4b0",
                    color: "#16a4b0",
                  }}
                  onClick={() => {
                    setShowResultImage((current) => !current);
                  }}
                  disabled={loading}
                >
                  {showResultImage ? "Show Original" : "Show Result"}
                </Button>
              )}
            </Box>
          </Box>
        )}

        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default V4PModalTestModel;
